import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { getPlayerProfile } from '../api/profile';
import {
  getFriends, getPendingRequests, subscribeToFriendships,
} from '../utils/supabaseActions';

/**
 * usePresence — online players list via Supabase Realtime Presence.
 * Zero DB egress for presence: a WebSocket channel carries live presence state.
 * Each client self-reports { username, is_in_game, room_code, rating, last_activity }.
 * Channel auto-cleans up on disconnect — no stale rows, no reconcile interval needed.
 *
 * @param {string|null} currentUser
 * @param {string} screen
 * @param {string} roomCode
 * @param {boolean} isSpectator
 */
export function usePresence(currentUser, screen, roomCode, isSpectator = false) {
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [onlinePlayersLoading, setOnlinePlayersLoading] = useState(Boolean(currentUser));
  const [onlinePlayersError, setOnlinePlayersError] = useState(false);
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(Boolean(currentUser));
  const [friendsError, setFriendsError] = useState(false);
  const [showPeopleModal, setShowPeopleModal] = useState(false);
  const [peopleModalTab, setPeopleModalTab] = useState('online');

  const channelRef = useRef(null);
  const ratingRef = useRef(1500);
  // Stable refs for screen/roomCode/isSpectator so track() can read latest without
  // dependency-array churn that would tear down and recreate the channel.
  const screenRef = useRef(screen);
  const roomCodeRef = useRef(roomCode);
  const isSpectatorRef = useRef(isSpectator);

  useEffect(() => { screenRef.current = screen; }, [screen]);
  useEffect(() => { roomCodeRef.current = roomCode; }, [roomCode]);
  useEffect(() => { isSpectatorRef.current = isSpectator; }, [isSpectator]);

  // Derive flat player list from Supabase Realtime presenceState() map.
  const derivePlayerList = useCallback((state) => {
    const now = Date.now();
    const list = Object.values(state)
      .flat()
      .filter(p => p && p.username)
      .map(p => {
        const lastActivityMs = p.last_activity
          ? new Date(p.last_activity).getTime()
          : now;
        const secondsAgo = Math.floor((now - lastActivityMs) / 1000);
        let statusLabel = 'Online';
        if (secondsAgo > 300) statusLabel = 'Idle';
        else if (secondsAgo < 60) statusLabel = 'Now';
        return {
          username: p.username,
          is_in_game: p.is_in_game || false,
          room_code: p.room_code || null,
          rating: p.rating || 1500,
          last_activity: p.last_activity || new Date().toISOString(),
          secondsAgo,
          statusLabel,
        };
      });
    // Deduplicate: same user on 2 devices → keep the most recently active entry
    const byUser = new Map();
    for (const p of list) {
      const existing = byUser.get(p.username);
      if (!existing || p.secondsAgo < existing.secondsAgo) {
        byUser.set(p.username, p);
      }
    }
    return Array.from(byUser.values());
  }, []);

  // Call channel.track() to publish/refresh own state.
  const trackSelf = useCallback(async () => {
    const ch = channelRef.current;
    if (!ch) return;
    try {
      await ch.track({
        username: currentUser,
        is_in_game: screenRef.current === 'game' && !isSpectatorRef.current,
        room_code: roomCodeRef.current || null,
        rating: ratingRef.current,
        last_activity: new Date().toISOString(),
      });
    } catch (e) {
      // Non-fatal — presence is best-effort
      console.error('Failed to track presence:', e);
    }
  }, [currentUser]);

  // ─── Realtime Presence channel (created once per login session) ───
  useEffect(() => {
    if (!currentUser) {
      setOnlinePlayers([]);
      setOnlinePlayersLoading(false);
      setOnlinePlayersError(false);
      return undefined;
    }

    setOnlinePlayersLoading(true);
    setOnlinePlayersError(false);

    // Fetch own rating once so it appears correctly to other clients.
    getPlayerProfile(currentUser)
      .then(profile => { if (profile?.rating) ratingRef.current = profile.rating; })
      .catch(() => {});

    const channel = supabase.channel('online-players', {
      config: { presence: { key: currentUser } },
    });
    channelRef.current = channel;

    const syncList = () => {
      setOnlinePlayers(derivePlayerList(channel.presenceState()));
      setOnlinePlayersLoading(false);
      setOnlinePlayersError(false);
    };

    channel
      .on('presence', { event: 'sync' }, syncList)
      .on('presence', { event: 'join' }, syncList)
      .on('presence', { event: 'leave' }, syncList)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          setOnlinePlayersLoading(false);
          setOnlinePlayersError(false);
          await trackSelf();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setOnlinePlayersLoading(false);
          setOnlinePlayersError(true);
        }
      });

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [currentUser, derivePlayerList, trackSelf]);

  // Re-track whenever screen / roomCode / isSpectator change — no channel re-creation.
  useEffect(() => {
    if (currentUser && channelRef.current) {
      trackSelf();
    }
  }, [screen, roomCode, isSpectator, trackSelf, currentUser]);

  // ─── Friends system ───
  const loadFriendsData = useCallback(async () => {
    if (!currentUser) return;

    setFriendsLoading(true);
    setFriendsError(false);

    try {
      const [f, p] = await Promise.all([
        getFriends(currentUser),
        getPendingRequests(currentUser),
      ]);
      setFriends(f);
      setPendingRequests(p);
    } catch (e) {
      console.error('Failed to load friends data:', e);
      setFriendsError(true);
    } finally {
      setFriendsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setFriends([]);
      setPendingRequests([]);
      setFriendsLoading(false);
      setFriendsError(false);
      return;
    }

    loadFriendsData();

    const unsubFriends = subscribeToFriendships(currentUser, () => {
      loadFriendsData();
    });

    return () => unsubFriends();
  }, [currentUser, loadFriendsData]);

  /** Untrack own presence (called on logout). Channel cleanup handled by useEffect. */
  const clearPresence = useCallback(async () => {
    const ch = channelRef.current;
    if (ch) {
      try { await ch.untrack(); } catch { /* best-effort */ }
    }
  }, []);

  /**
   * Re-fetch own rating from the DB and re-broadcast presence state.
   * Call this after a game ends so the updated ELO appears in the online list.
   */
  const refreshPresence = useCallback(async () => {
    if (!currentUser) return;
    try {
      const profile = await getPlayerProfile(currentUser);
      if (profile?.rating) ratingRef.current = profile.rating;
    } catch { /* best-effort */ }
    await trackSelf();
  }, [currentUser, trackSelf]);

  return {
    onlinePlayers, setOnlinePlayers,
    onlinePlayersLoading, onlinePlayersError,
    friends, pendingRequests,
    friendsLoading, friendsError,
    showPeopleModal, setShowPeopleModal,
    peopleModalTab, setPeopleModalTab,
    loadFriendsData,
    clearPresence,
    refreshPresence,
  };
}
